import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, t as translateFn } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Idioma predeterminado: español
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('cmp_language');
    return saved || 'es'; // Español por defecto
  });

  // País seleccionado (se sincroniza con el idioma)
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const saved = localStorage.getItem('cmp_country');
    return saved || 'Argentina';
  });

  // Guardar preferencias en localStorage
  useEffect(() => {
    localStorage.setItem('cmp_language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('cmp_country', selectedCountry);
  }, [selectedCountry]);

  // Cuando cambia el idioma, actualizar el país automáticamente
  useEffect(() => {
    if (language === 'pt') {
      setSelectedCountry('Brasil');
    } else if (language === 'es' && selectedCountry === 'Brasil') {
      // Si cambia a español y estaba en Brasil, volver a Argentina
      setSelectedCountry('Argentina');
    }
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'es' ? 'pt' : 'es');
  }, []);

  // Función helper para traducir - lee language directamente
  const t = (translationObj) => {
    return translateFn(translationObj, language);
  };

  // Función para obtener traducciones por sección
  const getSection = useCallback((section) => {
    const sectionData = translations[section];
    if (!sectionData) return {};
    
    const result = {};
    for (const [key, value] of Object.entries(sectionData)) {
      result[key] = value[language] || value.es || '';
    }
    return result;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t,
      getSection,
      selectedCountry,
      setSelectedCountry,
      isPortuguese: language === 'pt',
      isSpanish: language === 'es'
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
